import {isValidElement, useEffect, useRef} from 'react';

import {keyExistsInQueryString} from '../query-string';

const RENDER_LOGGING_ENABLED = keyExistsInQueryString('RENDER_LOGGING');

const RENDER_PERF_LOG_PREFIX = 'WB_R';

/**
 * Helps debug what props/state has changed reference equality, and warns
 * when a ref changes but the underlying data didn't. Useful for tracking
 * why a useMemo/useEffect fired, or why a React.memo() component
 * re-rendered.
 *
 * Requires `RENDER_LOGGING` in the query string (no value needed, e.g.
 * `?RENDER_LOGGING`).
 *
 * @param description - Label for this component in log output.
 * @param valObject - Object of named values to monitor for changes.
 *
 * @example
 * ```ts
 * // Pass individual props
 * useDetectChanges('MyComponent', {myPropName, myOtherPropName});
 *
 * // Pass the full props object
 * useDetectChanges('MyComponent', props);
 * ```
 */
export const useDetectChanges = (
  description: string,
  valObject: Record<string, unknown>
) => {
  const prev = useRef(valObject);

  useEffect(() => {
    if (!RENDER_LOGGING_ENABLED) {
      return;
    }

    console.log(`${RENDER_PERF_LOG_PREFIX} ${description} rendered`);
    // note that this doesn't try to detect if a key was removed/added. Since the
    // object you're passing in should be hard coded, that won't happen.
    Object.entries(valObject).forEach(([newKey, newVal]) => {
      const prevVal = prev.current[newKey];
      const prefix = `${RENDER_PERF_LOG_PREFIX} ${description}.${newKey}`;
      if (newVal !== prevVal) {
        const before = getDiffableStringForVal(prevVal);
        const after = getDiffableStringForVal(newVal);
        if (before === null || after === null) {
          console.log(
            `%c${prefix}: unable to get diffable string for value, likely due to circular reference in the object. See previous log for the actual value that caused this.`,
            `color: red; background: #f0f0f0;`
          );
          return;
        }
        if (before !== after) {
          console.log(`${prefix} changed:`);
          console.log(`${prefix}   before:`, before);
          console.log(`${prefix}   after:`, after);
        } else {
          console.log(`${prefix}`, before);
          console.log(
            `%c${prefix} ^^^ before and after are the same, but the object references are different.`,
            `color: red; background: #f0f0f0;`
          );
        }
      }
    });

    prev.current = valObject;
  });
};

/**
 * Logs a render event for class components, which cannot use hooks.
 *
 * Requires `RENDER_LOGGING` in the query string (no value needed, e.g.
 * `?RENDER_LOGGING`).
 *
 * @param componentName - Label for this component in log output.
 */
export const reportRenderInClassComponent = (componentName: string) => {
  if (!RENDER_LOGGING_ENABLED) {
    return;
  }
  console.log(
    `${RENDER_PERF_LOG_PREFIX} ${componentName} rendered, no prop change data available in this class component`
  );
};

let IDENT = 0;
const COMPONENT_IDENT_MAP = new WeakMap<any, string>();

function tryToGetComponentName(componentFn: any) {
  if (typeof componentFn === 'string') {
    return `<${componentFn}>`;
  }
  // COMPONENT_IDENT_MAP stores a map of the component function identity to an
  // id string that we can append to the component name to differentiate between
  // two components with the same displayName. This is to avoid the case where,
  // e.g. @wandb/components <Button> is confused with MUI <Button> or similar.
  // It's unlikely to ever happen, but the additional paranoia is cheap to
  // implement!
  if (!COMPONENT_IDENT_MAP.has(componentFn)) {
    COMPONENT_IDENT_MAP.set(componentFn, (++IDENT).toString(16));
  }
  const ident = COMPONENT_IDENT_MAP.get(componentFn);

  // Do our best to get the name of a React component function that's been
  // passed in as an arg to useDetectChanges. This is better than stringifying
  // the component function because they are hard to parse in the logs. Append
  // a unique identifier so that two components with the same name don't get
  // conflated by the comparison function.
  return `<${componentFn.displayName || componentFn.name || 'UNKNOWN'}::${ident}>`;
}

function replacer(_key: string, value: unknown) {
  // React elements have circular references embedded in their internal context,
  // so we need to convert them to something that JSON.stringify doesn't choke
  // on. This _could_ mean that we get false positives in rerenders, but it's
  // better than the alternative of not being able to compare React elements at
  // all.
  if (isValidElement(value as any)) {
    const val = value as any;
    return {
      key: val.key,
      props: val.props,
      ref: val.ref, // todo after React 19: val.props.ref
      type: tryToGetComponentName(val.type),
    };
  } else if (typeof value === 'function') {
    return value.toString();
  } else {
    return value;
  }
}

function getDiffableStringForVal(val: unknown) {
  try {
    return JSON.stringify(val, replacer);
  } catch (e) {
    console.log((e as Error).message, val);
    console.log(
      `%c${RENDER_PERF_LOG_PREFIX}: JSON stringify error`,
      `color: red; background: #f0f0f0`
    );
    return null;
  }
}
